import React from 'react';
import PropTypes from 'prop-types';
import { Navigate, } from 'react-router-dom';
import ContentPane from '../containers/ContentPaneContainer';
import Navbar from '../containers/NavbarContainer';
import AuthenticationService from '../service/AuthenticationService';
import TokenRefreshManager from '../modules/TokenRefreshManager';
import SDKPaymentView from './view/Sdk/SdkPaymentView';
import SDKService from '../service/SdkService';
import createIPBSAdapter from './view/IPBS/CreateIPBSView';
import ClientTransformService from '../service/ClientTransformService';
import PayableIntegrationService from '../service/PayableIntegrationService';
import createPayableIntegrationServiceView from './view/PayableIntegrationService/CreatePayableIntegrationServiceView';
import PropertyTaxService from '../service/PropertyTaxService';
import createPropertyTaxViewAdapter from './view/PropertyTax/createPropertyTaxViewAdapter';
import registeredViews from '../modules/RegisteredViews';
import './App.css';
import WaterService from '../service/WaterService';
import createWaterViewAdapter from './view/Water/createWaterViewAdapter';
import CitationsService from '../service/CitationsService';
import createCitationsViewAdapter from './view/Citations/createCitationsViewAdapter';

class App extends React.Component {
  constructor(props) {
    super(props);
    this.hasBootstrapped = false;
  }

  componentDidMount() {
    this.bootstrapViews();
  }

  componentDidUpdate() {
    // Mounting while locked (expired token) skips the bootstrap, so run it once
    // the session becomes valid again — e.g. after unlocking.
    this.bootstrapViews();
  }

  bootstrapViews() {
    if (this.hasBootstrapped || !AuthenticationService.isAuthenticated()) {
      return;
    }
    this.hasBootstrapped = true;

    // Login only mounts on the '/' route, so a direct load of '#/dashboard' would
    // otherwise never start the proactive refresh timer.
    TokenRefreshManager.start();

    const { initializeNavigationItems, registerViews, } = this.props;

    PayableIntegrationService.getPISServiceOfferings().then((response) => {
      const { services, } = response.data;
      services.forEach((service) => {
        registeredViews[service.soid] = createPayableIntegrationServiceView(service.soid, service.comboFields);
      });
    });

    ClientTransformService.getAllCTSServiceOfferings().then((response) => {
      const { services, } = response.data;
      services.forEach((service) => {
        registeredViews[service] = createIPBSAdapter(service);
      });
    });

    CitationsService.getServiceOfferings().then((response) => {
      const { services, } = response.data;
      // Remove gr_parking as this service has a custom view and will not use generic citations view
      const excludeSoid = [ 'gr_parking', ];
      const citationsServices = services.filter(service => !excludeSoid.includes(service));
      citationsServices.forEach((service) => {
        registeredViews[service] = createCitationsViewAdapter(service);
      });
    });

    WaterService.getServiceOfferings().then((response) => {
      const { services, } = response.data;
      // Remove stl water as this service has a custom view and will not use the water view
      const waterservices = services.filter(service => service !== 'stl_water');
      waterservices.forEach((service) => {
        registeredViews[service] = createWaterViewAdapter(service);
      });
    });

    PropertyTaxService.getAllPTAXServiceOfferings().then((response) => {
      const { services, } = response.data;
      services.forEach((service) => {
        registeredViews[service] = createPropertyTaxViewAdapter(service);
      });
    });

    SDKService.getAllSDKServiceOfferings().then((response) => {
      const { services, } = response.data;
      services.forEach((service) => {
        const SDKAdapter = () => (
          <SDKPaymentView serviceOfferingId={`${service}`} />
        );
        registeredViews[service] = SDKAdapter;
      });

      registerViews(registeredViews);
      initializeNavigationItems(this.props);
    });
  }

  render() {
    // Guard on session existence, not token validity: an expired token is
    // recoverable via the lock screen, and redirecting here would unmount the
    // dashboard (losing open tabs) instead of letting the lock screen overlay it.
    if (!AuthenticationService.hasSession()) {
      return <Navigate to="/" replace />;
    }

    return (
      <div className="wrapper">
        <Navbar />
        <ContentPane />
      </div>
    );
  }
}

App.propTypes = {
  initializeNavigationItems: PropTypes.instanceOf(Function).isRequired,
  registerViews: PropTypes.instanceOf(Function).isRequired,
};

export default App;
